/* eslint-disable no-param-reassign */
import { PayloadAction, asyncThunkCreator, buildCreateSlice } from '@reduxjs/toolkit';
import type { RootState } from '../app/types';
import WorthyAPI, { GeneralApiRes } from '../services/worthyAPI';
import { getAddressFromGoogle } from '../lib/googleMapsHelper';
import { isFakePhone } from '../lib/validations/phoneNumber';
import { GenerateShippingParams, SchedulingType } from '../types/scheduling';

const ValueCategory = {
  noTouch: 'no touch',
  lowTouch: 'low touch',
  standard: 'standard',
  hv: 'high value',
  ue: 'ultra expensive',
  se: 'super expensive',
};
const createAppSlice = buildCreateSlice({
  creators: { asyncThunk: asyncThunkCreator },
});

export const isUltraValueLeads = (item: Item | undefined) => {
  if (!item) return false;
  return item.valueCategory?.toLowerCase().includes(ValueCategory.ue);
};

const objToAddress = (obj: Pick<Address, 'city' | 'street' | 'state' | 'zipcode' | 'apt'>) => {
  const {
    street, city, state, zipcode, apt,
  } = obj;
  if (apt) {
    return `${street}, ${apt}, ${city}, ${state}, ${zipcode}`;
  }
  return `${street}, ${city}, ${state}, ${zipcode}`;
};

interface IShipBack {
  shippingAddress: string;
  shippingOption: string;
  locationId: string;
  locationName: string;
  comment: string;
}

interface SchedulingDetails {
  type: SchedulingType.DROPOFF | SchedulingType.PICKUP;
  localEndTimeString?: string;
  localStartTimeString: string;
  address: string;
  nearestFedExLocations?: string[];
  shipment?: {
    shipmentId: number | null;
    shippingLabelUrl: string;
  };
}

enum WorkflowStatus {
  PENDING = 'pending',
  SUBMITTED = 'submitted',
  SCHEDULING = 'scheduling',
  PENDING_APPROVAL = 'photo-approval-required',
  SHIPPING_SCHEDULED = 'shipping-scheduled',
  PENDING_IMAGE_UPLOAD = 'photo-required',
  INSURANCE_APPROVAL_REQUIRED = 'insurance-approval-required',
  IN_TRANSIT = 'in-transit',
  ARRIVED = 'arrived',
  PREP_FOR_SALE = 'prepare-for-sale',
  READY_FOR_AUCTION = 'ready-for-auction',
  AUCTION_SCHEDULED = 'auction-scheduled',
  LIVE_AUCTION = 'live-auction',
  AUCTION_ENDED = 'auction-ended',
  DEAL = 'deal',
  NOT_ARRIVED = 'not-arrived',
  REJECTED_IN_PREPARATION = 'rejected-in-preparation',
  REJECTED_AFTER_AUCTION = 'rejected-after-auction',
  RETURN_PENDING = 'return-pending',
  REJECTED_WITHOUT_SHIPBACK = 'rejected-without-shipback',
  RETURNED = 'returned',
  QUICK_SALE = 'quick-sale',
}
type AppointmentDetails = {
  rescheduleUrl: string;
  cancelUrl: string;
  startTime: string;
  endTime: string;
  uri: string;
} | null;
enum PhotoType {
  submission = 'submission',
  marketplace = 'marketplace',
}
interface PhotoDetails {
  documentId: number;
  url: string;
  type: PhotoType.marketplace | PhotoType.submission;
}

interface Item {
  userId: number;
  publicId: string;
  status: WorkflowStatus;
  bundleId: number;
  secondary?: boolean;
  group?: string;
  photos: PhotoDetails[];
  itemDetails: Record<string, string>; // +
  schedulingDetails: SchedulingDetails;
  itemDescription: string; // +
  itemType: string; // +
  createdAt: string;
  valueCategory: string;
  appointmentDetails: AppointmentDetails;
  auctionDetails?: {
    auctionId: number | null;
    startTime: string;
  };
  shipBack?: IShipBack;
}

interface LocationAddress {
  StreetLines: string;
  City: string;
  StateOrProvinceCode: string;
  PostalCode: string;
  CountryCode: string;
  Residential: boolean;
  GeographicCoordinates: string;
}

interface LocationType {
  location_id: string;
  location_type: string;
  name: string;
  address: LocationAddress;
  distance: {
    Value: string;
    Units: string;
  };
}

export interface PickupAvailability {
  cut_off_time: string;
  access_time: number;
  date: string;
}
export interface Slot {
  dayText: string;
  readyTimestamp: string;
  timeText: string;
  companyCloseTime: string;
  earlySlot: boolean;
}

export interface Day {
  dayText: string;
  dayNum: string;
  monthText: string;
  date: string;
  slots: Slot[];
}

interface ItemShippingType {
  data: {
    item: any;
    user: any;
  };
}

export interface Address {
  street: string;
  city: string;
  state: string;
  zipcode: string;
  apt: string;
  privateResidence: boolean;
  shipmentAddress: string;
}
export interface SchedulingState {
  shippingMethod: SchedulingType.PICKUP | SchedulingType.DROPOFF;
  pickupAvailability: PickupAvailability[];
  userLocalTime: string;
  address: Address;
  slot: Slot | null;
  stepper: {
    methodSelected: boolean;
    addressSelected: boolean;
    pickupTimeSelected: boolean;
  };
  locations: LocationType[];
  createShippingLoading: boolean;
  scheduleShippingLoading: boolean;
  scheduleShippingError: boolean;
  userPhoneNumber: string;
  scheduleSuccess: boolean;
}

const initialState: SchedulingState = {
  shippingMethod: SchedulingType.PICKUP,
  address: {
    street: '',
    city: '',
    state: '',
    zipcode: '',
    apt: '',
    privateResidence: false,
    shipmentAddress: '',
  },
  stepper: {
    methodSelected: true,
    addressSelected: false,
    pickupTimeSelected: false,
  },
  pickupAvailability: [],
  userLocalTime: '',
  slot: null,
  locations: [],
  createShippingLoading: false,
  scheduleShippingLoading: false,
  scheduleShippingError: false,
  userPhoneNumber: '',
  scheduleSuccess: false,
};

type DropOffLocations = Record<number, { name: string; address: string }>;

interface GoogleAddressResponse {
  results: { formatted_address: string }[];
}

const prepareDropOffLocations = async (
  shipmentLocations: LocationType[],
): Promise<DropOffLocations> => {
  const locations: DropOffLocations = {};

  try {
    if (shipmentLocations?.length === 0) {
      throw new Error('Shipment locations are empty. Unable to set drop off');
    }

    const dropOffLocations: GoogleAddressResponse[] = await Promise.all(
      shipmentLocations.map((loc) => getAddressFromGoogle(loc.address)),
    );

    dropOffLocations.forEach((dropOffLocation, idx: number) => {
      const formattedAddress = dropOffLocation.results[0]?.formatted_address;
      locations[idx] = {
        name: shipmentLocations[idx].name,
        address: formattedAddress,
      };
    });

    return locations;
  } catch (error) {
    console.error('Error fetching drop-off locations:', error);
    throw error;
  }
};

export const schedulingSlice = createAppSlice({
  name: 'scheduling',
  initialState,
  reducers: (create) => ({
    setShippingMethod: create.reducer(
      (state, action: PayloadAction<SchedulingType.DROPOFF | SchedulingType.PICKUP>) => {
        state.shippingMethod = action.payload;
        state.stepper.methodSelected = true;
      },
    ),
    setSlot: create.reducer((state, action: PayloadAction<Slot>) => {
      state.slot = action.payload;
      state.stepper.pickupTimeSelected = true;
    }),
    clearScheduleShippingError: create.reducer((state) => {
      state.scheduleShippingError = false;
    }),
    createShipping: create.asyncThunk<
    ItemShippingType['data'],
    GenerateShippingParams['addressObj']
    >(
      async (addressObj, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const submitState = state.submit;

        const response: ItemShippingType['data'] = await WorthyAPI.createShipment({
          itemPublicIds: [submitState.submitAPI.itemId],
          user: {
            first_name: submitState.user.userData.firstName,
            last_name: submitState.user.userData.lastName,
          },
          addressObj,
        });
        if (!response.item || !response.user) {
          throw new Error('Invalid item or user');
        }
        if (!response.item.shipment) {
          throw new Error('Shipment data is missing');
        }

        return { item: response.item, user: response.user };
      },
      {
        pending: (state, action) => {
          state.createShippingLoading = true;
          const addressObj = action.meta.arg;
          state.address.city = addressObj.city;
          state.address.state = addressObj.state;
          state.address.zipcode = addressObj.zipcode;
          state.address.street = addressObj.street;
          state.address.apt = addressObj.street2;
          state.address.privateResidence = !addressObj.street2;
        },
        fulfilled: (state, action: PayloadAction<ItemShippingType['data']>) => {
          state.createShippingLoading = false;
          const { shipment } = action.payload.item as any;
          const { user } = action.payload;

          state.pickupAvailability = shipment.pickup_availability;
          state.locations = shipment.locations;
          state.userLocalTime = user.local_time;

          state.address.shipmentAddress = objToAddress({
            street: state.address.street,
            apt: state.address.apt,
            city: state.address.city,
            state: state.address.state,
            zipcode: state.address.zipcode,
          });
          state.stepper.addressSelected = true;
        },
        rejected: (state) => {
          state.createShippingLoading = false;
        },
      },
    ),
    scheduleShipping: create.asyncThunk(
      async (payload, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const submitState = state.submit;

        const response: SchedulingDetails = await WorthyAPI.scheduleShipping({
          shippingType: state.scheduling.shippingMethod,
          itemIds: [submitState.submitAPI.itemId],
          itemId: submitState.submitAPI.itemId,
          data: {
            drop_off_locations: await prepareDropOffLocations(state.scheduling.locations),
            ...(state.scheduling.shippingMethod === SchedulingType.PICKUP
              ? {
                company_close_time: state.scheduling.slot?.companyCloseTime || '',
                ready_timestamp: state.scheduling.slot?.readyTimestamp || '',
              }
              : {
                itemPublicId: submitState.submitAPI.itemId,
              }),
          },
        });
        return response;
      },
      {
        pending: (state) => {
          state.scheduleShippingLoading = true;
          state.scheduleShippingError = false;
        },
        fulfilled: (state) => {
          state.scheduleShippingLoading = false;
          state.scheduleShippingError = false;
          state.scheduleSuccess = true;
        },
        rejected: (state) => {
          state.scheduleShippingLoading = false;
          state.scheduleShippingError = true;
        },
      },
    ),

    setSchedulingPhoneNumber: create.asyncThunk<GeneralApiRes['status'], string>(
      async (phoneNumber, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const submitState = state.submit;
        const response: GeneralApiRes['status'] = await WorthyAPI.updateUserPhoneNumber(
          phoneNumber,
          +submitState.user.userData.id,
        );
        return response;
      },
      {
        pending: (state) => {
          state.createShippingLoading = true;
        },
        fulfilled: (state) => {
          state.createShippingLoading = false;
        },
        rejected: (state) => {
          state.createShippingLoading = false;
        },
      },
    ),
  }),

  selectors: {
    getShippingMethod: (state) => state.shippingMethod,
    getAddress: (state) => state.address,
    getSlot: (state) => state.slot,
    getStepper: (state) => state.stepper,
    getPickupAvailability: (state) => state.pickupAvailability,
    getUserLocalTime: (state) => state.userLocalTime,
    getCreateShippingLoading: (state) => state.createShippingLoading,
    getScheduleShippingLoading: (state) => state.scheduleShippingLoading,
    getScheduleSuccess: (state) => state.scheduleSuccess,
    getScheduleShippingError: (state) => state.scheduleShippingError,
    getShipmentAddress: (state) => state.address.shipmentAddress,
    getChosenSlot: (state) => state.slot,
  },
});

export const {
  getShippingMethod,
  getAddress,
  getSlot,
  getStepper,
  getScheduleSuccess,
  getPickupAvailability,
  getUserLocalTime,
  getCreateShippingLoading,
  getScheduleShippingLoading,
  getScheduleShippingError,
  getShipmentAddress,
  getChosenSlot,
} = schedulingSlice.selectors;

export const getUserPhone = (state: RootState) => {
  const userPhone = state.scheduling.userPhoneNumber;
  if (userPhone) {
    return userPhone;
  }
  const phoneOnSubmit = state.submit.user.userData.phone;
  return isFakePhone(phoneOnSubmit) ? '' : phoneOnSubmit;
};
export const toShowPhoneInput = (state: RootState) => {
  const phoneOnSubmit = state.submit.user.userData.phone;
  return !phoneOnSubmit || isFakePhone(phoneOnSubmit);
};

export const {
  setShippingMethod,
  setSlot,
  clearScheduleShippingError,
  createShipping,
  scheduleShipping,
  setSchedulingPhoneNumber,
} = schedulingSlice.actions;
